import { useCallback, useState } from 'react'

import { useDebouncedValue } from './useDebouncedValue'

const DEFAULT_PAGE_SIZE = 25
const NO_OUTSIDE_FILTERS = {}

export type UsePagedListStateOptions<TFilters> = {
  initialFilters: TFilters
  pageSize?: number
  /** Overrides the shared debounce; the default suits a search box. */
  debounceMs?: number
  /** Search or filter values the caller keeps elsewhere, such as a Formik search form. */
  outsideFilters?: Record<string, unknown>
}

export type PagedListState<TFilters> = {
  page: number
  size: number
  /** What the search box shows: updates on every keystroke. */
  search: string
  /** What the request should carry: trimmed, and only once typing settles. */
  appliedSearch: string
  filters: TFilters
  onSearchChange: (search: string) => void
  onFiltersChange: (filters: TFilters) => void
  onPageChange: (page: number) => void
  onSizeChange: (size: number) => void
  /**
   * Back to page 1. Anything that narrows or reorders the list has to call it,
   * and sorting is the usual case - the hook does not own the sort, because the
   * four tables model theirs differently.
   */
  resetPage: () => void
  /** Moves a page past the end back to the last page, for example after its last row is removed. */
  keepPageInRange: (pageCount: number) => void
}

/**
 * Page, size, a debounced search term and a filter bag, with the rule every
 * paged table needs: a list that narrows or reorders starts again from page 1,
 * since the rows the reader was paging through are no longer the rows there.
 *
 * The filters are opaque - each table's shape differs, and the hook only ever
 * compares them to spot a change.
 */
export const usePagedListState = <TFilters>({
  initialFilters,
  pageSize = DEFAULT_PAGE_SIZE,
  debounceMs,
  outsideFilters = NO_OUTSIDE_FILTERS,
}: UsePagedListStateOptions<TFilters>): PagedListState<TFilters> => {
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(pageSize)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<TFilters>(initialFilters)

  const typedSearch = search.trim()
  // `immediate` drops the pending term, so typing again inside the window cannot re-apply it. The
  // guard below empties the applied term in the same render as the box, rather than an effect later,
  // so no request goes out still carrying what was just cleared.
  const settledSearch = useDebouncedValue(typedSearch, debounceMs, { immediate: (term) => term === '' })
  const appliedSearch = typedSearch === '' ? '' : settledSearch

  const resetPage = useCallback(() => setPage(1), [])

  // Keyed on the applied term rather than the typed one: the rows do not change
  // until the request does, so resetting per keystroke would move the reader off
  // a page that still holds what they are looking at.
  //
  // Adjusted while rendering rather than in an effect because callers read the page
  // and the applied term together to build one request. Resetting afterwards would
  // let a render commit the new term against the old page, fetching a page of the
  // narrowed list nobody asked for before page 1 arrived. React throws this render
  // away and re-runs it, so that pairing never reaches a caller's query.
  const [narrowedBy, setNarrowedBy] = useState({ appliedSearch, filters, outsideFilters })
  let currentPage = page
  // Compared by value: a filter bag rebuilt with the same contents is not a
  // change, and treating it as one would strand the reader on page 1.
  if (
    narrowedBy.appliedSearch !== appliedSearch ||
    !shallowEqualFilters(narrowedBy.filters, filters) ||
    !shallowEqualFilters(narrowedBy.outsideFilters, outsideFilters)
  ) {
    setNarrowedBy({ appliedSearch, filters, outsideFilters })
    if (page !== 1) {
      setPage(1)
      currentPage = 1
    }
  }

  const keepPageInRange = useCallback(
    (pageCount: number) => {
      const lastPage = Math.max(pageCount, 1)
      if (currentPage > lastPage) setPage(lastPage)
    },
    [currentPage],
  )

  const onSizeChange = useCallback(
    (nextSize: number) => {
      setSize(nextSize)
      // A larger page starting where the old one did would skip rows.
      setPage(1)
    },
    [setPage],
  )

  return {
    page: currentPage,
    size,
    search,
    appliedSearch,
    filters,
    onSearchChange: setSearch,
    onFiltersChange: setFilters,
    onPageChange: setPage,
    onSizeChange,
    resetPage,
    keepPageInRange,
  }
}

/**
 * Every filter bag here is a flat record of string arrays, so one level of
 * comparison settles it without asking callers to memoise what they pass.
 */
const shallowEqualFilters = <TFilters>(a: TFilters, b: TFilters): boolean => {
  if (a === b) return true
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false
  const keys = Object.keys(a) as (keyof TFilters)[]
  if (keys.length !== Object.keys(b).length) return false
  return keys.every((key) => {
    const left = a[key]
    const right = b[key]
    if (Array.isArray(left) && Array.isArray(right)) {
      return left.length === right.length && left.every((item, index) => item === right[index])
    }
    return left === right
  })
}
